import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ChartBar, Users, Barbell, Globe, Shield, CheckCircle, ListBullets, Flame, X, MagnifyingGlass } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
};

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className="tactical-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} weight="bold" className="text-volt" />
      <span className="label-mono text-muted-foreground">{label}</span>
    </div>
    <div className="font-display text-4xl font-black leading-none">{value.toLocaleString()}</div>
  </div>
);

const tooltipStyle = {
  backgroundColor: "#141517",
  border: "1px solid #272A2D",
  borderRadius: 0,
  fontSize: 12,
  color: "#F3F4F6",
};

const Chart30 = ({ title, series, testid, color = "#C2FF05" }) => {
  const data = (series || []).map((d) => ({ date: fmtDate(d.date), count: d.count }));
  const total = (series || []).reduce((s, d) => s + d.count, 0);
  return (
    <div className="tactical-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-mono text-volt">// {title}</div>
          <div className="text-xs text-muted-foreground mt-1">Last 30 days</div>
        </div>
        <div className="font-display text-2xl font-black" data-testid={`${testid}-total`}>{total}</div>
      </div>
      <div style={{ width: "100%", height: 160 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
            <CartesianGrid stroke="#1f2123" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: "#6b7280", fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: "#272A2D" }}
              interval={4}
            />
            <YAxis
              tick={{ fill: "#6b7280", fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: "#272A2D" }}
              allowDecimals={false}
              width={28}
            />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(194,255,5,0.06)" }} />
            <Bar dataKey="count" fill={color} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default function AdminAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [opsModalOpen, setOpsModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/admin/analytics")
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setErr(extractApiError(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-volt text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-4" data-testid="analytics-back">
        <ArrowLeft size={12} weight="bold" /> Back
      </button>

      <div className="flex items-center gap-3 mb-2">
        <ChartBar size={36} weight="bold" className="text-volt" />
        <div>
          <div className="label-mono text-muted-foreground">// COMMAND CENTER</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black leading-none">Analytics</h1>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-8">
        Live engagement metrics across every operator on Tactical Progression. Admin-only.
      </p>

      {loading && <div className="text-muted-foreground text-sm" data-testid="analytics-loading">Loading metrics...</div>}
      {err && <div className="tactical-card p-5 text-red-400 text-sm" data-testid="analytics-error">{err}</div>}

      {data && (
        <div className="space-y-6">
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="totals-row">
            <Tile icon={Users} label="OPERATORS" value={data.totals.users} testid="stat-users" />
            <Tile icon={Barbell} label="PLANS" value={data.totals.plans} testid="stat-plans" />
            <Tile icon={Globe} label="PUBLIC" value={data.totals.public_plans} testid="stat-public" />
            <Tile icon={Shield} label="SQUADS" value={data.totals.squads} testid="stat-squads" />
            <Tile icon={CheckCircle} label="COMPLETIONS" value={data.totals.completions} testid="stat-completions" />
            <Tile icon={ListBullets} label="TOTAL EXERCISES PROGRAMMED" value={data.totals.exercise_slots} testid="stat-slots" />
          </div>

          {/* Active users strip */}
          <div className="tactical-card p-5" data-testid="active-strip">
            <div className="label-mono text-volt mb-4">// ACTIVE OPERATORS</div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-xs text-muted-foreground mb-1">DAU · Today</div>
                <div className="font-display text-3xl font-black" data-testid="active-dau">{data.active.dau}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">WAU · 7d</div>
                <div className="font-display text-3xl font-black" data-testid="active-wau">{data.active.wau}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">MAU · 30d</div>
                <div className="font-display text-3xl font-black" data-testid="active-mau">{data.active.mau}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-4 leading-relaxed">
              An operator counts as active on a day if they logged at least one workout completion that day.
            </div>
          </div>

          {/* Daily Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Chart30 title="SIGNUPS" series={data.daily.signups} testid="chart-signups" color="#C2FF05" />
            <Chart30 title="PLANS CREATED" series={data.daily.plans_created} testid="chart-plans" color="#7DD3FC" />
            <Chart30 title="WORKOUTS LOGGED" series={data.daily.completions} testid="chart-completions" color="#F97316" />
          </div>

          {/* Top Operators + Top Exercises */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="tactical-card p-5" data-testid="top-operators">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Flame size={16} weight="fill" className="text-volt" />
                  <div className="label-mono text-volt">// TOP OPERATORS</div>
                </div>
                <button
                  onClick={() => setOpsModalOpen(true)}
                  className="text-xs uppercase tracking-widest text-muted-foreground hover:text-volt inline-flex items-center gap-1"
                  data-testid="view-all-operators-btn"
                >
                  View all <Users size={12} weight="bold" />
                </button>
              </div>
              {data.top_operators.length === 0 ? (
                <div className="text-xs text-muted-foreground">No completions logged yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground uppercase tracking-widest">
                      <th className="text-left font-normal pb-2">#</th>
                      <th className="text-left font-normal pb-2">Callsign</th>
                      <th className="text-right font-normal pb-2">Sessions</th>
                      <th className="text-right font-normal pb-2 hidden sm:table-cell">Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_operators.map((op, i) => (
                      <tr key={op.user_id} className="border-t border-border" data-testid={`top-op-row-${i}`}>
                        <td className="py-2 text-muted-foreground font-mono">{i + 1}</td>
                        <td className="py-2">
                          <div className="font-medium">{op.name || op.email.split("@")[0]}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[18ch]">{op.email}</div>
                        </td>
                        <td className="py-2 text-right font-display text-lg">{op.completions}</td>
                        <td className="py-2 text-right text-xs text-muted-foreground hidden sm:table-cell">{op.last_active || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="tactical-card p-5" data-testid="top-exercises">
              <div className="flex items-center gap-2 mb-4">
                <Barbell size={16} weight="fill" className="text-volt" />
                <div className="label-mono text-volt">// MOST PROGRAMMED</div>
              </div>
              {data.top_exercises.length === 0 ? (
                <div className="text-xs text-muted-foreground">No exercises programmed yet.</div>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const max = Math.max(...data.top_exercises.map((e) => e.count), 1);
                    return data.top_exercises.map((ex, i) => (
                      <div key={ex.exercise_id} data-testid={`top-ex-row-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="truncate pr-2">
                            <span className="text-muted-foreground font-mono mr-2">{i + 1}.</span>
                            {ex.name}
                          </div>
                          <div className="font-display tabular-nums">{ex.count}</div>
                        </div>
                        <div className="h-1.5 bg-background border border-border">
                          <div className="h-full bg-volt" style={{ width: `${(ex.count / max) * 100}%` }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="text-xs text-muted-foreground text-right">
            Generated: <span data-testid="generated-at">{new Date(data.generated_at).toLocaleString()}</span>
          </div>
        </div>
      )}

      {opsModalOpen && <AllOperatorsModal onClose={() => setOpsModalOpen(false)} />}
    </Layout>
  );
}

function AllOperatorsModal({ onClose }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("completions");

  useEffect(() => {
    api.get("/admin/operators")
      .then((r) => setList(r.data))
      .catch((e) => setErr(extractApiError(e)));
  }, []);

  // Lock body scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const filtered = list ? list.operators.filter((op) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (op.name || "").toLowerCase().includes(needle) || (op.email || "").toLowerCase().includes(needle);
  }) : [];

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "completions") return b.completions - a.completions;
    if (sortKey === "joined") return (b.joined_at || "").localeCompare(a.joined_at || "");
    if (sortKey === "last_active") return (b.last_active || "").localeCompare(a.last_active || "");
    if (sortKey === "plans") return b.plan_count - a.plan_count;
    if (sortKey === "name") return (a.name || a.email).localeCompare(b.name || b.email);
    return 0;
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
  };

  const SortChip = ({ k, label }) => (
    <button
      onClick={() => setSortKey(k)}
      className={`px-3 py-1.5 text-xs uppercase tracking-widest border whitespace-nowrap ${
        sortKey === k ? "border-volt text-volt" : "border-border text-muted-foreground hover:border-volt/50"
      }`}
      data-testid={`sort-${k}`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4"
      onClick={onClose}
      data-testid="all-operators-modal"
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-card border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <div className="label-mono text-volt">// ALL OPERATORS</div>
            <h2 className="font-display text-2xl font-black mt-1">
              {list ? `${list.total} ${list.total === 1 ? "operator" : "operators"} on the platform` : "Loading..."}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-volt p-1"
            data-testid="close-operators-modal"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Search + sort */}
        <div className="px-5 pt-4 pb-3 space-y-3 border-b border-border">
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-background border border-border pl-9 pr-3 py-2 text-sm outline-none focus:border-volt"
              data-testid="operators-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs label-mono text-muted-foreground self-center">SORT:</span>
            <SortChip k="completions" label="Sessions" />
            <SortChip k="plans" label="Plans" />
            <SortChip k="last_active" label="Last active" />
            <SortChip k="joined" label="Newest" />
            <SortChip k="name" label="A-Z" />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {err && <div className="p-5 text-red-400 text-sm">{err}</div>}
          {!list && !err && <div className="p-5 text-muted-foreground text-sm">Loading roster...</div>}
          {list && sorted.length === 0 && <div className="p-5 text-muted-foreground text-sm">No matches.</div>}
          {sorted.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-card sticky top-0 border-b border-border">
                <tr className="text-xs text-muted-foreground uppercase tracking-widest">
                  <th className="text-left font-normal p-3">#</th>
                  <th className="text-left font-normal p-3">Operator</th>
                  <th className="text-right font-normal p-3 hidden sm:table-cell">Plans</th>
                  <th className="text-right font-normal p-3">Sessions</th>
                  <th className="text-right font-normal p-3 hidden md:table-cell">Last active</th>
                  <th className="text-right font-normal p-3 hidden md:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((op, i) => (
                  <tr
                    key={op.user_id}
                    className="border-b border-border/50 hover:bg-secondary/40"
                    data-testid={`op-row-${op.user_id}`}
                  >
                    <td className="p-3 text-muted-foreground font-mono text-xs">{i + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 font-medium">
                        {op.name || op.email.split("@")[0]}
                        {op.role === "admin" && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-volt text-volt">
                            ADMIN
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[28ch]">{op.email}</div>
                    </td>
                    <td className="p-3 text-right hidden sm:table-cell font-mono tabular-nums">{op.plan_count}</td>
                    <td className="p-3 text-right font-mono tabular-nums">
                      <span className={op.completions > 0 ? "text-foreground" : "text-muted-foreground"}>
                        {op.completions}
                      </span>
                    </td>
                    <td className="p-3 text-right hidden md:table-cell text-xs text-muted-foreground">{fmtDate(op.last_active)}</td>
                    <td className="p-3 text-right hidden md:table-cell text-xs text-muted-foreground">{fmtDate(op.joined_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-3 border-t border-border text-xs text-muted-foreground text-center">
          {list && `Showing ${sorted.length} of ${list.total} · click outside or press X to close`}
        </div>
      </div>
    </div>
  );
}
